/**
 * Homografie (perspectiefmapping) voor veldkalibratie.
 * H 3x3 (row-major, h33 = 1) beeldpixel -> courthoek in meters.
 * Pure wiskunde: geen DOM-afhankelijkheden.
 */

import type { FieldPoint } from "./types";

export type Mat3 = number[]; // lengte 9, row-major

/** Los 8x8 lineair stelsel op via Gauss-eliminatie met partiële pivotering. */
function solve8(m: number[][], b: number[]): number[] | null {
  const n = 8;
  const a = m.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) pivot = r;
    }
    if (Math.abs(a[pivot][col]) < 1e-12) return null;
    if (pivot !== col) {
      const tmp = a[col];
      a[col] = a[pivot];
      a[pivot] = tmp;
    }
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = a[r][col] / a[col][col];
      if (Math.abs(f) < 1e-15) continue;
      for (let c = col; c <= n; c++) a[r][c] -= f * a[col][c];
    }
  }
  const x = new Array(n);
  for (let i = 0; i < n; i++) x[i] = a[i][n] / a[i][i];
  return x;
}

/**
 * Computeer homografie die `src` (beeld, pixels) afbeeldt op `dst` (veld, meters).
 * Beide arrays moeten exact 4 punten hebben (bijv. TL, TR, BR, BL).
 */
export function computeHomography(src: FieldPoint[], dst: FieldPoint[]): Mat3 | null {
  if (src.length !== 4 || dst.length !== 4) return null;
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const { x: u, y: v } = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }
  const h = solve8(A, b);
  if (!h) return null;
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

export function applyHomography(H: Mat3, p: FieldPoint): FieldPoint {
  const { x, y } = p;
  const w = H[6] * x + H[7] * y + H[8];
  const px = (H[0] * x + H[1] * y + H[2]) / w;
  const py = (H[3] * x + H[4] * y + H[5]) / w;
  return { x: px, y: py };
}

export function invertHomography(H: Mat3): Mat3 | null {
  const [a, b, c, d, e, f, g, h, i] = H;
  const A = e * i - f * h;
  const B = c * h - b * i;
  const C = b * f - c * e;
  const D = f * g - d * i;
  const E = a * i - c * g;
  const F = c * d - a * f;
  const G = d * h - e * g;
  const Hh = b * g - a * h;
  const I = a * e - b * d;
  const det = a * A + b * D + c * G;
  if (Math.abs(det) < 1e-12) return null;
  const inv = [A / det, B / det, C / det, D / det, E / det, F / det, G / det, Hh / det, I / det];
  return inv;
}

export function dist(a: FieldPoint, b: FieldPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Kalibratie van de vier veldhoeken (TL, TR, BR, BL) naar FIVB-afmetingen. */
export function cornersToCourt(points: FieldPoint[], widthM: number, heightM: number): { H: Mat3; Hinv: Mat3 | null } | null {
  if (points.length !== 4) return null;
  const court: FieldPoint[] = [
    { x: 0, y: 0 },
    { x: widthM, y: 0 },
    { x: widthM, y: heightM },
    { x: 0, y: heightM },
  ];
  const H = computeHomography(points, court);
  if (!H) return null;
  return { H, Hinv: invertHomography(H) };
}
